import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, Subscription, timer } from 'rxjs';

import { DocAssessStatus } from '../../../../core/status/doc-assess.types';
import { DocDropComponent, DocDropType, DocUpload, uploadErrorMessage } from '../doc-drop/doc-drop';
import { DocVerdictComponent } from '../doc-verdict/doc-verdict';

/**
 * The minimal document shape this panel needs — a subset both `KsDocument` and
 * `DfFacilityDocument` satisfy, so one panel backs KS and DF alike.
 */
export interface PanelDoc<T extends string> {
  short_id: string;
  doc_type: T;
  file_name: string;
  assess_status: DocAssessStatus;
  assess_message: string;
}

/** A parent-supplied backend call, kept product-agnostic. */
export type UploadFn<T extends string> = (
  shortId: string,
  file: File,
  docType: T,
) => Observable<PanelDoc<T>>;
export type DeleteFn = (shortId: string, docShortId: string) => Observable<void>;
export type FetchDocsFn<T extends string> = (shortId: string) => Observable<PanelDoc<T>[]>;

/**
 * Shared document-upload orchestration for the KS/DF application surfaces.
 *
 * Owns the whole upload lifecycle — drag/drop → upload → the server-side AI
 * verdict poll → remove — so each host page (create wizard, detail page) no
 * longer re-implements it. The presentational {@link DocDropComponent} /
 * {@link DocVerdictComponent} stay unchanged inside.
 *
 * Two modes, via {@link editable}: drop blocks (the applicant can add/remove)
 * or a read-only verdict list (submitted application). The panel polls for
 * pending AI verdicts in **both** modes — fixing the bug where a document
 * uploaded on the detail page sat at "Checking…" until a full page reload,
 * because that page never polled (only the create wizard did).
 *
 * The host owns the backend calls (passed as {@link uploadFn}/{@link deleteFn}/
 * {@link fetchDocsFn}) and the surrounding card/title/hint chrome; this panel
 * owns only the mechanism.
 */
@Component({
  selector: 'app-doc-upload-panel',
  imports: [DocDropComponent, DocVerdictComponent],
  templateUrl: './doc-upload-panel.html',
  styleUrl: './doc-upload-panel.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocUploadPanelComponent<T extends string> {
  private readonly destroyRef = inject(DestroyRef);

  /** Buckets to show as drop blocks (and to group uploads by). */
  readonly docTypes = input.required<DocDropType<T>[]>();
  /** The KS application / DF facility short_id. Null until the draft exists. */
  readonly shortId = input<string | null>(null);
  /** Drop blocks (true) vs a read-only verdict list (false). */
  readonly editable = input(true);
  /** Greys the drop blocks out (e.g. until the business's consent is given); the list still shows. */
  readonly disabled = input(false);
  /** Documents already on the record — seeded once when the record loads. */
  readonly initialDocs = input<PanelDoc<T>[]>([]);
  readonly uploadFn = input.required<UploadFn<T>>();
  readonly deleteFn = input.required<DeleteFn>();
  readonly fetchDocsFn = input.required<FetchDocsFn<T>>();

  /** Emitted whenever an upload starts/finishes — the host blocks Submit on it. */
  readonly inFlightChange = output<boolean>();
  /** The settled (server-stored) documents — the host's "what will be submitted". */
  readonly attachedDocsChange = output<PanelDoc<T>[]>();
  /** Names of files that never reached the server — a host names them at review. */
  readonly failedUploadsChange = output<string[]>();

  /** All uploads (seeded + new), keyed by a client-side id. */
  protected readonly uploads = signal<DocUpload<T>[]>([]);
  /** Ids whose server-side delete is in flight (disables their remove button). */
  protected readonly deletingIds = signal<Set<string>>(new Set());

  // Seed once per record: the effect tracks shortId, and re-seeding is guarded
  // so a later host state change (e.g. its own document-list update) can't clobber
  // in-flight uploads this panel now owns.
  private seededForShortId: string | null = null;

  constructor() {
    effect(() => {
      const sid = this.shortId();
      const docs = this.initialDocs();
      if (!sid || this.seededForShortId === sid) return;
      this.seededForShortId = sid;
      this.uploads.set(docs.map((d) => this.toRow(d)));
      // A resumed record may still be waiting on a verdict.
      if (this.hasPendingVerdict()) this.ensurePolling(sid);
    });

    // Mirror internal state out to the host after every change.
    effect(() => {
      const list = this.uploads();
      this.inFlightChange.emit(list.some((u) => u.state === 'uploading'));
      this.attachedDocsChange.emit(
        list.filter((u) => u.doc !== null).map((u) => u.doc as PanelDoc<T>),
      );
      this.failedUploadsChange.emit(list.filter((u) => u.state === 'error').map((u) => u.fileName));
    });
  }

  protected uploadsByType(docType: T): DocUpload<T>[] {
    return this.uploads().filter((u) => u.docType === docType);
  }

  private toRow(doc: PanelDoc<T>): DocUpload<T> {
    return {
      id: doc.short_id,
      docType: doc.doc_type,
      fileName: doc.file_name,
      state: 'done',
      doc,
    };
  }

  protected onFilesPicked(docType: T, files: File[]): void {
    const sid = this.shortId();
    if (!sid || this.disabled()) return;
    for (const file of files) {
      this.uploadOne(sid, docType, file);
    }
  }

  private uploadOne(shortId: string, docType: T, file: File): void {
    const id = `${docType}-${file.name}-${file.size}-${this.uploads().length}`;
    this.uploads.update((list) => [
      ...list,
      { id, docType, fileName: file.name, state: 'uploading', doc: null },
    ]);

    this.uploadFn()(shortId, file, docType)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (doc) => {
          this.patchUpload(id, { state: 'done', doc });
          // The AI verdict runs off the request thread, so a fresh upload comes
          // back PENDING ("Checking…"). Poll until every verdict has landed.
          this.ensurePolling(shortId);
        },
        error: (err: unknown) => {
          this.patchUpload(id, { state: 'error', error: uploadErrorMessage(err) });
        },
      });
  }

  private patchUpload(id: string, patch: Partial<DocUpload<T>>): void {
    this.uploads.update((list) => list.map((u) => (u.id === id ? { ...u, ...patch } : u)));
  }

  protected removeUpload(id: string): void {
    const upload = this.uploads().find((u) => u.id === id);
    const sid = this.shortId();
    const docShortId = upload?.doc?.short_id;

    if (!upload || !sid || !docShortId) {
      // Never reached the server (upload error / still pending) — drop locally.
      this.uploads.update((list) => list.filter((u) => u.id !== id));
      return;
    }

    this.deletingIds.update((s) => new Set(s).add(id));
    this.deleteFn()(sid, docShortId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.uploads.update((list) => list.filter((u) => u.id !== id));
          this.clearDeleting(id);
        },
        error: () => this.clearDeleting(id),
      });
  }

  private clearDeleting(id: string): void {
    this.deletingIds.update((s) => {
      const next = new Set(s);
      next.delete(id);
      return next;
    });
  }

  // --- AI verdict polling ---------------------------------------------------
  // One shared loop (not one per file) re-fetches the record's documents and
  // patches any verdict that has landed, stopping once nothing is PENDING or the
  // attempt budget is spent (so rows fall back to "Checking…" rather than spin
  // forever).

  private pollSub: Subscription | null = null;
  private pollAttempts = 0;
  private static readonly POLL_INTERVAL_MS = 2500;
  private static readonly POLL_MAX_ATTEMPTS = 8;

  private ensurePolling(shortId: string): void {
    if (this.pollSub) return;
    this.pollAttempts = 0;
    this.pollSub = timer(
      DocUploadPanelComponent.POLL_INTERVAL_MS,
      DocUploadPanelComponent.POLL_INTERVAL_MS,
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.pollOnce(shortId));
  }

  private stopPolling(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = null;
  }

  private hasPendingVerdict(): boolean {
    return this.uploads().some(
      (u) => u.state === 'done' && (u.doc?.assess_status ?? 'PENDING') === 'PENDING',
    );
  }

  private pollOnce(shortId: string): void {
    if (!this.hasPendingVerdict()) {
      this.stopPolling();
      return;
    }
    if (++this.pollAttempts > DocUploadPanelComponent.POLL_MAX_ATTEMPTS) {
      this.stopPolling();
      return;
    }
    this.fetchDocsFn()(shortId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (docs) => {
          this.applyVerdicts(docs);
          if (!this.hasPendingVerdict()) this.stopPolling();
        },
        // A transient fetch error shouldn't kill the loop — retry next tick.
        error: () => undefined,
      });
  }

  private applyVerdicts(docs: PanelDoc<T>[]): void {
    const byId = new Map(docs.map((d) => [d.short_id, d]));
    this.uploads.update((list) =>
      list.map((u) => {
        const fresh = u.doc ? byId.get(u.doc.short_id) : undefined;
        if (!fresh || !u.doc) return u;
        return {
          ...u,
          doc: {
            ...u.doc,
            assess_status: fresh.assess_status,
            assess_message: fresh.assess_message,
          },
        };
      }),
    );
  }
}
